import { configureStore } from '@reduxjs/toolkit';

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { merge } from 'lodash';
import config from './config';
import * as Sentry from '@sentry/react';

import rootReducer, { initialState } from './reducers/graph';
import { RootState, VersionState } from './types/graph';
import { scrubSentryAction, scrubSentryState } from './util/sentryState';

const STORE_VERSION = 'v2';

const customStateReconciler = (
  inboundState: RootState,
  originalState: RootState,
) => {
  inboundState.queryVariant = undefined;
  inboundState.searchParams = {};
  const reconciled = merge({}, originalState, inboundState);
  if (
    originalState.queryVariant !== undefined &&
    originalState.queryVariant !== ''
  ) {
    reconciled.consultation.variant = undefined;
  }
  if (
    originalState.previousQueryVariant !== undefined &&
    originalState.previousQueryVariant !== ''
  ) {
    reconciled.previousQueryVariant = originalState.previousQueryVariant;
  }
  return reconciled;
};

export const generateStore = (
  kbVersion: number,
  versionData: VersionState,
  queryVariant?: string,
) => {
  const persistConfig = {
    key: `${config.appVersion}-${STORE_VERSION}-${kbVersion}`,
    version: kbVersion,
    blacklist: ['kb'],
    storage,
    stateReconciler: customStateReconciler,
  };

  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: scrubSentryAction,
    stateTransformer: scrubSentryState,
  });

  const searchParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }

  const preloadedState = {
    ...initialState,
    version: versionData,
    queryVariant,
    previousQueryVariant: queryVariant,
    searchParams: params,
    previousSearchParams: params,
    referrer: params['ref'] || undefined,
  };

  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
    devTools: !config.env.production,
    enhancers: (defaultEnhancers) => {
      return defaultEnhancers.concat(sentryReduxEnhancer);
    },
  });

  const persistor = persistStore(store);

  return { store, persistor };
};
